
import { CheckCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUserRoles } from "@/hooks/useUserRoles";

interface MissionActionsProps {
  missionId: string;
  onDelete: () => void;
  isCompleted: boolean;
  canModifyMission: boolean;
}

export const MissionActions = ({ missionId, onDelete, isCompleted, canModifyMission }: MissionActionsProps) => {
  const { toast } = useToast();
  const { roles } = useUserRoles();
  const isClient = roles.includes('client');
  const isAdmin = roles.includes('admin');

  // Allow both admins and clients to modify missions
  const canModify = canModifyMission || isAdmin || isClient;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const { error } = await supabase
      .from('missions')
      .delete()
      .eq('id', missionId);

    if (error) {
      console.error('Error deleting mission:', error);
      toast({
        title: "Error",
        description: "Failed to delete mission",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Mission deleted successfully",
    });
    onDelete();
  };

  const handleComplete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const { error } = await supabase
      .from('missions')
      .update({ status: 'completed' })
      .eq('id', missionId);

    if (error) {
      console.error('Error completing mission:', error);
      toast({
        title: "Error",
        description: "Failed to complete mission",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Mission marked as completed",
    });
    onDelete();
  };

  if (!canModify) return null;

  return (
    <div className="grid grid-cols-2 gap-2">
      {!isCompleted && (
        <Button
          variant="outline"
          size="sm"
          className="h-9 text-green-600 hover:text-green-700 hover:bg-green-50 border-2 border-green-200 shadow-sm w-full flex items-center justify-center gap-2 px-3"
          onClick={handleComplete}
        >
          <CheckCircle className="h-4 w-4" />
          <span className="whitespace-nowrap">Complete</span>
        </Button>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-destructive hover:text-destructive hover:bg-destructive/10 border-2 border-destructive/20 shadow-sm w-full flex items-center justify-center gap-2 px-3"
            onClick={(e) => e.stopPropagation()}
          >
            <Trash2 className="h-4 w-4" />
            <span className="whitespace-nowrap">Delete</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Mission</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this mission? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
